import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  Divider,
  Button,
  IconButton,
  TextField,
  Table, 
  TableBody, 
  TableRow, 
  TableCell, 
  InputAdornment
} from "@material-ui/core";
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
} from "@material-ui/icons";
import TextareaAutosize from "@material-ui/core/TextareaAutosize";
import CreateQuestion from "../products/createQuestion";
import AttachFileIcon from "@material-ui/icons/AttachFile";

const textAreaStyle = {
  width: "100%",
  minHeight: "60px",
  padding: "12px",
  borderRadius: "4px",
  border: "1px solid #ccc",
  outline: "none",
  fontSize: "16px",
  fontFamily: "inherit",
  resize: "vertical",
};

const imageUploadStyles = {
  uploadContainer: {
    border: '2px dashed #ccc',
    borderRadius: '4px',
    padding: '20px',
    textAlign: 'center',
    cursor: 'pointer',
    '&:hover': {
      border: '2px dashed #666'
    }
  },
  previewContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%'
  },
  imageWrapper: {
    position: 'relative',
    width: '100%',
    maxWidth: '300px',
    marginBottom: '8px'
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    padding: '4px 8px',
    borderRadius: '4px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  fileName: {
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  fileInput: {
    display: 'none'
  }
};

const styles = {
  priceSection: {
    width: '100%',
    maxWidth: '800px',
    margin: '20px auto'
  },
  table: {
    width: '100%',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    padding: '16px',
    marginTop: '16px'
  },
  tableRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid #e0e0e0'
  },
  tableRowLast: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    fontWeight: 'bold'
  },
  label: {
    flex: 1
  },
  amount: {
    textAlign: 'right',
    marginRight: '8px'
  }
};

const FormProduct = ({ productForm, setProductForm, onValidate }) => {
  const [questions, setQuestions] = useState(productForm.questions || []);
  const [urls, setUrls] = useState(productForm.URL || [""]);
  const [imgPreview, setImgPreview] = useState(productForm.imgPreview || null);
  const [file, setFile] = useState(productForm.imgFile || null);
  const [errors, setErrors] = useState({});
  const [quantity, setQuantity] = useState(productForm.quantity || 1);

  const formRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!productForm.URL || productForm.URL.length === 0) {
      setUrls([""]);
      setProductForm((prev) => ({
        ...prev,
        URL: [""],
      }));
    } else {
      setUrls(productForm.URL);
    }
  }, [productForm.URL]);
  

  useEffect(() => {
    setImgPreview(productForm.imgPreview);
  }, [productForm.imgPreview]);

  const handleInputChange = (key, value) => {
    setProductForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleAddUrl = () => {
    setUrls([...urls, ""]);
  };

  const handleRemoveUrl = (index) => {
    const updatedUrls = urls.filter((_, i) => i !== index);
    setUrls(updatedUrls);
    setProductForm((prev) => ({
      ...prev,
      URL: updatedUrls,
      quantity: updatedUrls.some(url => url.trim() !== "") ? 1 : prev.quantity,
    }));
  };

  const handleUrlChange = (index, value) => {
    const updatedUrls = urls.map((url, i) => (i === index ? value : url));
    setUrls(updatedUrls);

    const isUrlEntered = updatedUrls.some(url => url.trim() !== "");
    
    setProductForm(prev => ({
      ...prev,
      URL: updatedUrls,
      quantity: isUrlEntered ? 1 : prev.quantity || 1,
    }));
  };

  const handleQuantityChange = (e) => {
    let value = e.target.value.replace(/[^0-9]/g, ""); 
    value = Number(value);

    if (urls.some(url => url.trim() !== "")) {
      value = 1;
    } else {
      if (value < 1) value = "";
      if (value > 1000) value = 1000;
    }

    setQuantity(value);
    handleInputChange("quantity", value);
  };

  useEffect(() => {
    if (urls.some(url => url.trim() !== "")) {
      handleInputChange("quantity", 1);
    }
  }, [urls]);

  const handleQuestionAdd = (question) => {
    setQuestions([...questions, question]);
    handleInputChange("questions", [...questions, question]);
  };

  const handleQuestionRemove = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
    handleInputChange("questions", questions.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    let newErrors = {};
  
    if (!productForm.title) newErrors.title = "必須事項です";
    if (!productForm.mailtext) newErrors.mailtext = "必須事項です";
  
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleValidation = () => {
    if (validateForm()) {
      onValidate();
    }
  };

if (!productForm) {
  console.error("productForm が `undefined` になっています！");
  return <Typography color="error">エラー: productForm がありません</Typography>;
}

const handleDeleteImage = () => {
  setImgPreview(null);
  setFile(null);
  handleInputChange("imgPreview", null);
  handleInputChange("imgFile", null);
};

const handleFileChange = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // ファイル情報をstateに保存
  setFile(file);

  // FileReaderでプレビュー用のURLを生成
  const reader = new FileReader();
  reader.onloadend = () => {
    setImgPreview(reader.result);
    handleInputChange("imgPreview", reader.result);
    handleInputChange("imgFile", file);
  };
  reader.readAsDataURL(file);
};

  return (
    <Box 
      ref={formRef} 
      noValidate 
      autoComplete="off"
      sx={{ maxWidth: '800px', margin: '0 auto', padding: '24px' }}
    >
      <Box mb={3}>
        <TextField
          label="商品名"
          fullWidth
          required
          value={productForm.title}
          onChange={(e) => handleInputChange("title", e.target.value)}
          inputProps={{ maxLength: 100 }}
          error={!!errors.title}
          helperText={errors.title}
        />
      </Box>

      <Box my={2}>
        {imgPreview ? (
          <Box textAlign="center">
            <img
              src={imgPreview}
              alt="プレビュー"
              style={{
                maxWidth: "100%",
                maxHeight: "300px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
            <Box
              display="flex"
              alignItems="center"
              borderBottom="2px solid #1976d2"
              paddingY={1}
              paddingX={1}
              mt={2}
            >
              <AttachFileIcon style={{ color: "#1976d2", marginRight: 4 }} />
              <Typography
                variant="body2"
                color="textPrimary"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flexGrow: 1,
                }}
              >
                {file.name} ({(file.size / 1024).toFixed(1)} kB)
              </Typography>
              <IconButton
                size="small"
                onClick={handleDeleteImage}
                style={{ color: "#1976d2", marginLeft: 4 }}
              >
                ×
              </IconButton>
            </Box>
          </Box>
        ) : (
          <Box
            display="flex"
            alignItems="center"
            onClick={() => fileInputRef.current?.click()}
            style={{ 
              cursor: "pointer", 
              borderBottom: "2px solid #ccc", 
              paddingBottom: "4px"
            }}
          >
            <AttachFileIcon color="action" style={{ marginRight: 8 }} />
            <Typography variant="body2" color="textSecondary">
              商品の紹介画像
            </Typography>
          </Box>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </Box>

      {/* 価格情報 - 既存のコードを若干整理 */}
      <Box my={4}>
        <Typography variant="body2" color="textSecondary">
          価格(300円以上、ピッタプット利用料と決済手数料を含む税込価格)
        </Typography>
        <TextField
          fullWidth
          type="number"
          value={productForm.rawPrice}
          onChange={(e) => {
            let value = e.target.value;

            if (value.length > 1 && value.startsWith("0")) {
              value = value.replace(/^0+/, "");
            }

            setProductForm((prev) => ({
              ...prev,
              rawPrice: value ? Number(value) : "",
            }));
          }}
          margin="normal"
          inputProps={{ min: 300 }}
          InputProps={{
            endAdornment: <InputAdornment position="end">円</InputAdornment>,
          }}
        />

        <Table style={{ width: "100%", maxWidth: 400, margin: "0 auto" }}>
          <TableBody>
            <TableRow>
              <TableCell style={{ textAlign: "left", padding: "18px", whiteSpace: "nowrap" }}>
                ピッタプット利用料
              </TableCell>
              <TableCell style={{ textAlign: "right", padding: "18px", whiteSpace: "nowrap" }}>
                {productForm.platformFee} 円
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ textAlign: "left", padding: "18px", whiteSpace: "nowrap" }}>
                決済手数料
              </TableCell>
              <TableCell style={{ textAlign: "right", padding: "18px", whiteSpace: "nowrap" }}>
                {Math.floor(productForm.rawPrice * productForm.feeRate)} 円
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                style={{
                  textAlign: "left",
                  padding: "18px",
                  borderBottom: "none",
                  whiteSpace: "nowrap",
                }}
              >
                利益（振込額）
              </TableCell>
              <TableCell
                style={{
                  textAlign: "right",
                  padding: "18px",
                  borderBottom: "none",
                  whiteSpace: "nowrap",
                }}
              >
                {productForm.rawPrice -
                  productForm.platformFee -
                  Math.floor(productForm.rawPrice * productForm.feeRate)}{" "}
                円
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      {/* 以降は既存のコードをそのまま使用 */}
      <Divider />
      <Box mb={2}>
        <TextareaAutosize
          minRows={5}
          placeholder="商品の説明"
          value={productForm.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
          style={textAreaStyle}
        />
      </Box>
      <Box my={4}>
      {urls.map((url, index) => (
        <Box key={index} display="flex" alignItems="center" mb={2}>
          <TextField
            fullWidth
            placeholder="商品URL"
            value={url}
            onChange={(e) => handleUrlChange(index, e.target.value)}
            margin="normal"
          />
          <Box display="flex" alignItems="center" ml={2}>
            {index > 0 && (
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => handleRemoveUrl(index)}
              >
                削除
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              size="small"
              style={{ marginLeft: index > 0 ? 8 : 0 }}
              onClick={handleAddUrl}
            >
              追加
            </Button>
          </Box>
        </Box>
      ))}
    </Box>

      <Box mb={2}>
        <TextField
          label="購入個数"
          type="number"
          required
          fullWidth
          value={productForm.quantity || ""}
          onChange={(e) => handleQuantityChange(e)}
          disabled={urls.some(url => url.trim() !== "")} // URLが入力されたら変更不可
          inputProps={{ min: 1, max: 1000 }}
          error={!!errors.quantity}
          helperText={errors.quantity}
        />
      </Box>
      <Box mb={2}>
        <TextareaAutosize
          minRows={5}
          placeholder="メール文面"
          value={productForm.mailtext}
          onChange={(e) => handleInputChange("mailtext", e.target.value)}
          style={{
            ...textAreaStyle,
            border: errors.mailtext ? "2px solid red" : "1px solid #ccc",
          }}
        />
        {errors.mailtext && (
          <Typography variant="caption" style={{ color: "red" }}>
            {errors.mailtext}
          </Typography>
        )}
      </Box>
      <Divider />
      <Box mb={2}>
        <TextareaAutosize
          minRows={5}
          placeholder="備考"
          value={productForm.note}
          onChange={(e) => handleInputChange("note", e.target.value)}
          style={textAreaStyle}
        />
      </Box>
      {questions.map((question, index) => (
        <Box key={index} mb={2}>
          <CreateQuestion
            question={question}
            onRemove={() => handleQuestionRemove(index)}
          />
        </Box>
      ))}
      {/* <Button startIcon={<AddIcon />} onClick={() => handleQuestionAdd({ label: "", type: "" })}>
        質問を追加
      </Button> */}
      <Box mt={4} textAlign="center">
        <Button color="primary" variant="contained" onClick={handleValidation}>
          内容確認
        </Button>
      </Box>
    </Box>
  );
};

export default FormProduct;